<template>
  <div class="page page--company-tech-support">
    <div class="page__title">
      <div class="page__title-icon">
        <img
          :src="$options.SUPPORT_ICON"
          :alt="$t('tech-support.page.title')"
        />
      </div>
      <div class="page__title-text">{{ $t('tech-support.page.title') }}</div>
    </div>
    <div class="company-tech-support">
      <div class="company-tech-support__information">
        <div class="company-tech-support__information-title">
          {{ $t('tech-support.information.title') }}
        </div>
        <div class="company-tech-support__information-text">
          {{ $t('tech-support.information.text') }}
        </div>

        <div class="company-tech-support__contact">
          <div class="company-tech-support__contact-title">
            {{ $t('tech-support.contact.messengers') }}
          </div>
          <div class="company-tech-support__contact-messengers">
            <div
              class="company-tech-support__contact-messengers-item company-tech-support__contact-messengers-item--tg"
              @click="goToTelegramChat"
            >
              <div class="company-tech-support__contact-messengers-item-name">
                Telegram
              </div>
              <div class="company-tech-support__contact-messengers-item-icon">
                <img :src="$options.TELEGRAM_ICON" />
              </div>
            </div>
          </div>
          <div class="company-tech-support__contact-email">
            <div class="company-tech-support__contact-email-title">
              {{ $t('tech-support.contact.email') }}
            </div>
            <a
              :href="`mailto:${$options.SUPPORT_EMAIL}`"
              class="company-tech-support__contact-email-value"
            >
              {{ $options.SUPPORT_EMAIL }}
            </a>
          </div>
        </div>
      </div>
      <div class="company-tech-support__form" v-if="!feedbackSent">
        <loader
          class="company-tech-support__form-loader"
          v-if="isRequestProcessing"
        ></loader>
        <div class="company-tech-support__form-title">
          {{ $t('tech-support.form.title') }}
        </div>
        <div class="company-tech-support__form-fields">
          <custom-select
            class="company-tech-support__form-field"
            mode="big"
            :title="$t('tech-support.form.fields.topic')"
            v-model:value="supportData.topic"
            :options="$options.AVAILABLE_TOPICS"
            value-key="id"
          ></custom-select>
          <text-input
            class="company-tech-support__form-field"
            :title="$t('tech-support.form.fields.contact-email')"
            :placeholder="
              $t('tech-support.form.fields.contact-email-placeholder', { email: 'email@gmail.com' })
            "
            v-model:value="supportData.email"
          ></text-input>
          <file-input
            class="company-tech-support__form-field"
            :title="$t('tech-support.form.fields.file')"
            :placeholder="$t('tech-support.form.fields.file-placeholder')"
            v-model:value="supportData.file"
          ></file-input>
          <text-area-input
            v-model:value="supportData.message"
            class="company-tech-support__form-field"
            :title="$t('tech-support.form.fields.message')"
            :placeholder="$t('tech-support.form.fields.message-placeholder')"
          ></text-area-input>
        </div>
        <primary-button
          class="company-tech-support__form-action"
          :label="$t('tech-support.actions.send-feedback')"
          @click="sendSupportFeedback"
          :disabled="isRequestProcessing || v$.$silentErrors.length > 0"
        ></primary-button>
      </div>
      <div
        class="company-tech-support__feedback-received"
        v-else
        v-html="$t('tech-support.feedback-sent')"
      ></div>
    </div>
  </div>
</template>

<script>
import { SupportService } from '@js-app/services/support/support-service'
import SupportIcon from '@js-app/assets/page-icons/support.svg'
import TelegramIcon from '@js-app/assets/support/tg.svg'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import TextAreaInput from '@vue-app/components/base/input/TextAreaInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import FileInput from '@vue-app/components/base/input/FileInput.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { fileValidator } from '@js-app/services/validators'
import { mapState } from 'vuex'
import Loader from '@vue-app/components/base/Loader.vue'

export default {
  name: 'CompanyTechSupport',
  components: {
    Loader,
    FileInput,
    PrimaryButton,
    TextAreaInput,
    TextInput,
    CustomSelect,
  },

  setup: () => ({
    v$: useVuelidate(),
  }),
  data() {
    return {
      supportData: {
        topic: null,
        email: '',
        file: null,
        message: '',
      },
      isRequestProcessing: false,
      feedbackSent: false,
    }
  },
  validations() {
    return {
      supportData: {
        topic: { required },
        email: { required },
        file: {
          required,
          fileValidator: fileValidator([
            'image/png',
            'image/jpg',
            'image/jpeg',
            'image/JPG',
            'image/PNG',
          ]),
        },
        message: {
          required,
        },
      },
    }
  },

  SUPPORT_EMAIL: SupportService.SUPPORT_EMAIL,
  SUPPORT_ICON: SupportIcon,
  TELEGRAM_ICON: TelegramIcon,
  AVAILABLE_TOPICS: SupportService.AVAILABLE_JIRA_TOPICS,

  computed: {
    ...mapState(['selectedCompany']),
  },
  methods: {
    goToTelegramChat() {
      SupportService.goToTelegramChat()
    },
    async sendSupportFeedback() {
      this.isRequestProcessing = true
      try {
        await SupportService.createJiraTicket(
          this.selectedCompany,
          this.supportData
        )
        this.feedbackSent = true
      } catch (e) {
      } finally {
        this.isRequestProcessing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--company-tech-support {
    margin-top: 28px;
  }
}
.company-tech-support {
  display: grid;
  grid-template-columns: 1fr 390px;
  column-gap: 115px;
  &__information {
    margin-top: 55px;
    margin-left: 40px;
    max-width: 597px;

    &-title {
      font-weight: 700;
      font-size: 40px;
      line-height: 46px;
      color: #585f6d;
      margin-bottom: 25px;
    }

    &-text {
      font-weight: 500;
      font-size: 16px;
      line-height: 28px;
      color: #67768a;
    }
  }

  &__contact {
    margin-top: 43px;
    padding: 33px 40px 48px;
    background: #ffffff;
    border-radius: 22px;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-title {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      color: #666d79;
    }

    &-messengers {
      margin-top: 20px;
      display: flex;
      align-items: center;
      justify-content: center;

      &-item {
        display: flex;
        align-items: center;
        padding: 6px 6px 6px 21px;
        background: #37aee2;
        border-radius: 48px;
        cursor: pointer;

        &:hover {
          opacity: 0.8;
        }

        &-name {
          font-weight: 600;
          font-size: 14px;
          line-height: 24px;
          color: #ffffff;
        }

        &-icon {
          width: 35px;
          height: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 20px;
        }
      }
    }

    &-email {
      margin-top: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;

      &-title {
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        text-align: center;
        color: #666d79;
      }

      &-value {
        font-weight: 500;
        font-size: 18px;
        line-height: 20px;
        text-align: center;
        color: #64748B;
        text-decoration: none;
        margin-top: 19px;
      }
    }
  }

  &__form {
    position: relative;
    margin-top: 58px;
    background: #ffffff;
    border-radius: 22px;
    padding: 32px 50px 36px;

    &-title {
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
      color: #666d79;
    }

    &-fields {
      margin-top: 38px;
      margin-bottom: 27px;
    }

    &-field {
      margin-bottom: 30px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-action {
      padding: 25px;
    }

    &-loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.7);
      z-index: 3;
    }
  }

  &__feedback-received {
    margin-top: 58px;
    background: #ffffff;
    border-radius: 22px;
    padding: 32px 50px 36px;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    color: #666d79;
    text-align: center;
    align-self: center;
  }
}
</style>
